import { useCallback, useEffect, useState } from "react";
import { ApiError, apiFetch } from "@/lib/api-client";

export interface PageCommentRecord {
  id: string;
  pageId: string;
  pageVersionId: string;
  anchorType: "page" | "component";
  componentId: string | null;
  componentType: string | null;
  position: { x: number; y: number } | null;
  content: string;
  authorId: string;
  authorName: string;
  authorEmail: string | null;
  parentId: string | null;
  resolved: boolean;
  resolvedAt: string | null;
  resolvedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PageVersionRefResponse {
  data: {
    currentVersionId: string | null;
  };
}

async function resolveCurrentVersionId(pageId: string): Promise<string> {
  const response = await apiFetch<PageVersionRefResponse>(`/crm/pages/${pageId}`);
  const versionId = response.data.currentVersionId;

  if (!versionId) {
    throw new Error("Save the page once before adding review comments.");
  }

  return versionId;
}

export async function fetchPageComments(pageId: string): Promise<PageCommentRecord[]> {
  const response = await apiFetch<{ data: { items: PageCommentRecord[] } }>(
    `/crm/pages/${pageId}/comments?includeResolved=true&limit=200`,
  );

  return response.data.items ?? [];
}

export async function addPageComment(
  pageId: string,
  input: {
    anchorType: "page" | "component";
    componentId?: string | null;
    componentType?: string | null;
    position?: { x: number; y: number } | null;
    content: string;
    pageVersionId?: string | null;
  },
): Promise<PageCommentRecord> {
  const pageVersionId = input.pageVersionId ?? await resolveCurrentVersionId(pageId);
  const response = await apiFetch<{ data: PageCommentRecord }>(
    `/crm/pages/${pageId}/comments`,
    {
      method: "POST",
      body: JSON.stringify({
        pageVersionId,
        anchorType: input.anchorType,
        componentId: input.componentId ?? null,
        componentType: input.componentType ?? null,
        position: input.position ?? null,
        content: input.content,
      }),
    },
  );

  return response.data;
}

export async function resolvePageComment(
  pageId: string,
  commentId: string,
): Promise<PageCommentRecord> {
  const response = await apiFetch<{ data: PageCommentRecord }>(
    `/crm/pages/${pageId}/comments/${commentId}/resolve`,
    {
      method: "POST",
    },
  );

  return response.data;
}

export async function replyToPageComment(
  pageId: string,
  commentId: string,
  input: {
    content: string;
  },
): Promise<PageCommentRecord> {
  const response = await apiFetch<{ data: PageCommentRecord }>(
    `/crm/pages/${pageId}/comments/${commentId}/reply`,
    {
      method: "POST",
      body: JSON.stringify({
        content: input.content,
      }),
    },
  );

  return response.data;
}

export function usePageComments(pageId: string | undefined) {
  const [comments, setComments] = useState<PageCommentRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!pageId || pageId === "new") {
      setComments([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      setComments(await fetchPageComments(pageId));
    } catch (nextError) {
      setComments([]);
      setError(nextError instanceof ApiError ? nextError.message : "Failed to load page comments");
    } finally {
      setIsLoading(false);
    }
  }, [pageId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createComment = useCallback(
    async (input: {
      anchorType: "page" | "component";
      componentId?: string | null;
      componentType?: string | null;
      position?: { x: number; y: number } | null;
      content: string;
      pageVersionId?: string | null;
    }) => {
      if (!pageId) {
        throw new Error("Save the page first before adding review comments.");
      }

      const created = await addPageComment(pageId, input);
      setComments((current) => [created, ...current]);
      return created;
    },
    [pageId],
  );

  const markResolved = useCallback(
    async (commentId: string) => {
      if (!pageId) {
        throw new Error("Page comments are unavailable until the page is saved.");
      }

      const resolved = await resolvePageComment(pageId, commentId);
      setComments((current) =>
        current.map((comment) => (comment.id === commentId ? resolved : comment)),
      );
      return resolved;
    },
    [pageId],
  );

  const replyToComment = useCallback(
    async (commentId: string, input: { content: string }) => {
      if (!pageId) {
        throw new Error("Page comments are unavailable until the page is saved.");
      }

      const reply = await replyToPageComment(pageId, commentId, input);
      setComments((current) => [reply, ...current]);
      return reply;
    },
    [pageId],
  );

  return {
    comments,
    isLoading,
    error,
    refresh,
    createComment,
    markResolved,
    replyToComment,
  };
}
